import { useMutation } from '@tanstack/react-query'
import { ChevronRight, Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useRouteSession } from '@web/app'
import { authClient } from '@web/auth'
import { BrandMark } from '@layerit/ui/components/brand-mark'
import { ProfileMenu } from '@web/components/profile-menu'
import { ThemeMenu } from '@layerit/ui/components/theme-menu'
import { Alert, AlertDescription } from '@layerit/ui/components/ui/alert'
import { Button } from '@layerit/ui/components/ui/button'
import { Skeleton } from '@layerit/ui/components/ui/skeleton'
import { useOrganizations } from '@web/hooks/organizations'
import { cn } from '@layerit/ui/lib/utils'
import { ROUTES, isSafeRedirectPath } from '@web/router'
import { APP_NAME } from '@layerit/brand'

export function initialsOf(name: string): string {
	const words = name
		.split(/[\s@._-]+/)
		.map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
		.filter(Boolean)
	const [first, second] = words
	if (first === undefined) return '?'
	if (second === undefined) return first.slice(0, 1).toUpperCase()
	return `${first.slice(0, 1)}${second.slice(0, 1)}`.toUpperCase()
}

const MARK_TINTS = [
	'bg-slate-100 text-slate-700 group-hover:bg-slate-200',
	'bg-stone-100 text-stone-700 group-hover:bg-stone-200',
	'bg-zinc-100 text-zinc-700 group-hover:bg-zinc-200',
	'bg-neutral-100 text-neutral-700 group-hover:bg-neutral-200',
	'bg-gray-100 text-gray-700 group-hover:bg-gray-200',
	'bg-indigo-50 text-indigo-700 group-hover:bg-indigo-100',
	'bg-sky-50 text-sky-800 group-hover:bg-sky-100',
	'bg-teal-50 text-teal-800 group-hover:bg-teal-100',
] as const

export function markTint(seed: string): string {
	let hash = 0x811c9dc5
	for (const character of seed) {
		hash ^= character.codePointAt(0)!
		hash = Math.imul(hash, 0x01000193) >>> 0
	}
	hash = Math.imul(hash ^ (hash >>> 16), 0x21f0aaad) >>> 0
	hash = Math.imul(hash ^ (hash >>> 15), 0x735a2d97) >>> 0
	hash = (hash ^ (hash >>> 15)) >>> 0
	return MARK_TINTS[hash % MARK_TINTS.length] ?? MARK_TINTS[0]
}

export function redirectTarget(search: string): string {
	const requested = new URLSearchParams(search).get('redirect')
	return requested !== null && isSafeRedirectPath(requested) ? requested : ROUTES.getStarted
}

export function OrganisationChooser() {
	const { search } = useLocation()
	const { state, reload } = useRouteSession()
	const target = redirectTarget(search)

	const organizations = useOrganizations()

	function settle() {
		void reload()
	}

	const [opening, setOpening] = useState<string | null>(null)

	const activate = useMutation({
		mutationFn: async (organizationId: string) => {
			const result = await authClient.organization.setActive({ organizationId })
			if (result.error) throw new Error(result.error.message ?? 'That organisation could not be opened.')
			return result
		},
		onSuccess: () => {
			settle()
		},
	})

	const [query, setQuery] = useState('')
	const roster = organizations.data ?? []
	const visible = useMemo(() => {
		const needle = query.trim().toLowerCase()
		if (needle === '') return roster
		return roster.filter(
			(organization) => organization.name.toLowerCase().includes(needle) || organization.slug.toLowerCase().includes(needle),
		)
	}, [roster, query])
	const only = roster.length === 1 ? roster[0] : undefined

	useEffect(() => {
		if (!only || activate.isPending || activate.isSuccess || activate.isError) return
		activate.mutate(only.id)
	}, [only, activate])

	if (organizations.isSuccess && roster.length === 0) {
		return <Navigate to={`${ROUTES.newOrganisation}?redirect=${encodeURIComponent(target)}`} replace />
	}

	const active =
		state.status === 'ready' || (state.status === 'organisation' && state.activeOrganizationId !== null && state.reason === null)
	if (active) return <Navigate to={target} replace />

	if (organizations.isPending) {
		return (
			<div className="flex flex-col gap-6" aria-busy>
				<div className="flex flex-col gap-2">
					<Skeleton className="h-7 w-56" />
					<Skeleton className="h-4 w-72" />
				</div>
				<div className="flex flex-col gap-2">
					<Skeleton className="h-14 w-full" />
					<Skeleton className="h-14 w-full" />
				</div>
				<p className="sr-only" role="status">
					Loading your organisations…
				</p>
			</div>
		)
	}

	if (organizations.isError) {
		return (
			<div className="flex flex-col gap-5">
				<header className="flex flex-col gap-2">
					<h1 className="font-display text-2xl text-ink">Your organisations could not be loaded</h1>
					<p className="text-sm leading-relaxed text-ink-3">
						{APP_NAME} could not read the list of organisations this account belongs to, so there is nothing to choose from yet.
					</p>
				</header>
				<Alert variant="destructive">
					<AlertDescription>
						{organizations.error instanceof Error ? organizations.error.message : 'The request failed.'}
					</AlertDescription>
				</Alert>
				<Button type="button" variant="primary" className="h-10 w-fit" onClick={() => void organizations.refetch()}>
					Try again
				</Button>
			</div>
		)
	}

	if (only) {
		return (
			<div className="flex flex-col gap-2">
				<Skeleton className="h-7 w-56" />
				<p role="status" className="mt-4 text-sm text-ink-3">
					Opening {only.name}…
				</p>
			</div>
		)
	}

	return (
		<div className="flex flex-col">
			<header className="flex flex-col gap-2">
				<h1 className="font-display text-2xl text-ink">Which organisation?</h1>
				<p className="text-sm leading-relaxed text-ink-3">
					This account belongs to more than one. The one you pick is the organisation the console opens into.
				</p>
			</header>

			{activate.isError && (
				<Alert variant="destructive" className="mt-6">
					<AlertDescription>
						{activate.error instanceof Error && activate.error.message !== ''
							? activate.error.message
							: 'That organisation could not be opened. Try again.'}
					</AlertDescription>
				</Alert>
			)}

			{roster.length > 5 && (
				<div className="relative mt-7">
					<Search aria-hidden className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-3" />
					<input
						id="organisation-search"
						type="search"
						aria-label="Search organisations"
						placeholder="Search organisations"
						autoComplete="off"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						className="h-11 w-full rounded-md border border-input bg-card pr-3 pl-9 text-base outline-none transition-colors placeholder:text-ink-3 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/40 md:text-sm"
					/>
				</div>
			)}

			<ul aria-label="Your organisations" className="mt-5 flex h-[328px] flex-col gap-0.5 overflow-y-auto overflow-x-hidden">
				{visible.map((organization) => (
					<li key={organization.id}>
						<button
							type="button"
							aria-busy={opening === organization.id || undefined}
							disabled={activate.isPending}
							aria-label={`Open ${organization.name}`}
							className="group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-inset focus-visible:outline-none disabled:opacity-50"
							onClick={() => {
								setOpening(organization.id)
								activate.mutate(organization.id)
							}}
						>
							<span
								aria-hidden
								className={cn(
									'grid size-9 shrink-0 place-items-center rounded-md text-xs font-medium transition-colors',
									markTint(organization.id),
								)}
							>
								{initialsOf(organization.name)}
							</span>

							<span className="flex min-w-0 flex-1 flex-col gap-0.5">
								<span className="truncate text-sm font-medium text-ink">
									{organization.name}
									{opening === organization.id && <span className="text-ink-3">…</span>}
								</span>
								<span className="record truncate text-ink-3">{organization.slug}</span>
							</span>

							<ChevronRight aria-hidden className="size-4 shrink-0 text-ink-3 transition-colors group-hover:text-ink" />
						</button>
					</li>
				))}
			</ul>

			{visible.length === 0 && <p className="mt-4 text-sm text-ink-3">No organisation matches “{query.trim()}”.</p>}

			<Link
				className="mt-5 w-fit text-xs font-medium text-ink-3 underline underline-offset-2 hover:text-ink"
				to={ROUTES.newOrganisation}
			>
				Create a new organisation
			</Link>
		</div>
	)
}

export function AccountShell({ children }: { children?: React.ReactNode }) {
	const { state, signOut } = useRouteSession()
	const navigate = useNavigate()

	if (state.status !== 'ready' && state.status !== 'organisation') return null

	return (
		<div className="flex min-h-screen flex-col bg-surface">
			<div className="flex shrink-0 items-center justify-between gap-4 px-6 py-6">
				<span className="flex items-center gap-2.5">
					<BrandMark size={28} />
					<span className="text-sm font-semibold tracking-tight text-ink">{APP_NAME}</span>
				</span>
				<div className="flex items-center gap-1">
					<ThemeMenu />
					<ProfileMenu user={state.user} signOut={signOut} navigate={navigate} />
				</div>
			</div>

			<div className="mx-auto flex w-full max-w-104 flex-1 flex-col justify-center px-6 pb-12">{children ?? <Outlet />}</div>
		</div>
	)
}
